import React from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  HStack,
  Badge,
  Button,
} from '@chakra-ui/react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';

const Confidentialite = () => {
  const sections = [
    {
      id: 'responsable',
      title: 'Responsable du traitement',
      emoji: 'ℹ️',
      items: [
        'Groupe Solarenov – 276 avenue du Prado, 13008 Marseille.',
        'SIREN : à compléter – RCS Marseille.',
        'Téléphone : 04 01 00 11 18.',
        'Contact principal : contact@groupe-solarenov.com.',
        'Représentant légal : Direction Groupe Solarenov.',
      ],
    },
    {
      id: 'donnees',
      title: 'Données collectées',
      emoji: '🧾',
      items: [
        'Identité et coordonnées : nom, prénom, email, téléphone.',
        'Informations sur le logement : surface, type d’équipement, besoins spécifiques.',
        'Préférences de contact et historique des échanges.',
        'Données techniques de navigation strictly nécessaires (pages visitées, temps de réponse).',
        'Pièces jointes transmises volontairement (devis, photos, factures).',
      ],
    },
    {
      id: 'finalites',
      title: 'Finalités du traitement',
      emoji: '🎯',
      items: [
        'Analyser votre projet de rénovation énergétique et vous orienter vers la meilleure solution.',
        'Organiser la prise de contact avec nos conseillers et partenaires certifiés.',
        'Assurer le suivi administratif, la constitution des dossiers d’aides et la planification des chantiers.',
        'Envoyer des informations pédagogiques ou commerciales relatives à nos services (avec votre accord).',
        'Mesurer la satisfaction et améliorer l’expérience utilisateur du site.',
      ],
    },
    {
      id: 'bases',
      title: 'Bases légales',
      emoji: '⚖️',
      items: [
        'Article 6.1.b RGPD : exécution de mesures précontractuelles (analyse de votre demande).',
        'Article 6.1.a RGPD : consentement pour les communications marketing.',
        'Article 6.1.f RGPD : intérêt légitime pour sécuriser la plateforme et prévenir la fraude.',
      ],
    },
    {
      id: 'duree',
      title: 'Durée de conservation',
      emoji: '⏱️',
      items: [
        '24 mois après le dernier échange commercial, sauf obligation légale différente.',
        'En cas de signature d’un contrat, conservation le temps de la relation puis archivage légal (jusqu’à 10 ans).',
        'Les cookies de mesure d’audience sont conservés 13 mois maximum.',
      ],
    },
    {
      id: 'partage',
      title: 'Partage et sous-traitance',
      emoji: '🤝',
      items: [
        'Aucune revente de données à des tiers.',
        'Transmission strictement nécessaire aux partenaires techniques (hébergeur, CRM, installateurs certifiés).',
        'Tous les sous-traitants sont contractuellement tenus au respect du RGPD et basés dans l’UE ou en zone adéquate.',
      ],
    },
    {
      id: 'securite',
      title: 'Sécurité des données',
      emoji: '🛡️',
      items: [
        'Hébergement sécurisé en Union européenne avec sauvegardes redondées.',
        'Chiffrement TLS des formulaires et accès restreints par authentification forte.',
        'Revue régulière des droits d’accès et journalisation des opérations critiques.',
      ],
    },
    {
      id: 'droits',
      title: 'Vos droits RGPD',
      emoji: '🧑‍⚖️',
      items: [
        'Accès, rectification, effacement et limitation.',
        'Opposition aux traitements ou à la prospection.',
        'Portabilité des données fournies.',
        'Possibilité de définir des directives post-mortem (article 40-1 loi Informatique & Libertés).',
        'Réclamation auprès de la CNIL (www.cnil.fr) en cas de litige.',
      ],
    },
  ];

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)">
      <Header />
      <Box as="main" flex="1" pt={["100px", "100px", "90px", "90px"]}>
        <Box
          left="50%"
          ml="-50vw"
          w="100vw"
          position="relative"
          bg="linear-gradient(135deg, #0ea5e9 0%, #6366f1 50%, #8b5cf6 100%)"
          py={{ base: 14, lg: 18 }}
        >
          <Container maxW="1200px" px={5}>
            <Heading as="h1" size={{ base: 'xl', md: '2xl' }} color="white" fontWeight={900} textAlign="center">
              🔒 Politique de confidentialité
            </Heading>
            <Text mt={4} color="white" fontSize={{ base: 'md', md: 'lg' }} textAlign="center" opacity={0.85}>
              Transparence complète sur la manière dont Groupe Solarenov protège vos données.
            </Text>
          </Container>
        </Box>

        <Container maxW="1200px" px={5} py={12}>
          <Box bg="white" borderRadius="3xl" p={{ base: 6, md: 10 }} boxShadow="0 25px 70px rgba(30,64,175,0.12)" position="relative" overflow="hidden">
            <Box
              position="absolute"
              top="-120px"
              right="-120px"
              w="240px"
              h="240px"
              bg="linear-gradient(135deg, rgba(99,102,241,0.15), rgba(14,165,233,0.05))"
              borderRadius="full"
              filter="blur(0px)"
            />
            <Stack spacing={10} position="relative">
              <Stack spacing={3}>
                <Badge
                  alignSelf="flex-start"
                  bg="rgba(14,165,233,0.1)"
                  color="teal.600"
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  fontWeight={600}
                  fontSize="xs"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                >
                  Protection des données
                </Badge>
                <Heading as="h2" size={{ base: 'lg', md: 'xl' }} color="gray.800" fontWeight={800} lineHeight={1.2}>
                  Vos informations personnelles restent entre de bonnes mains
                </Heading>
                <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }} maxW="4xl">
                  Cette politique détaille les engagements de Groupe Solarenov concernant la collecte, l’utilisation, la conservation et la sécurisation de vos données personnelles.
                </Text>
              </Stack>

              <Box borderTop="1px solid" borderColor="gray.100" />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }}>
                {sections.map((section) => (
                  <Box
                    key={section.id}
                    border="1px solid"
                    borderColor="rgba(79,70,229,0.12)"
                    borderRadius="2xl"
                    p={{ base: 5, md: 6 }}
                    bg="linear-gradient(135deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 70%)"
                    boxShadow="0 12px 35px rgba(15,23,42,0.06)"
                    transition="all 0.3s ease"
                    _hover={{ transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(59,130,246,0.18)' }}
                  >
                    <HStack spacing={3} align="center" mb={4}>
                      <Box
                        w={12}
                        h={12}
                        borderRadius="xl"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg="linear-gradient(135deg, rgba(14,165,233,0.18), rgba(99,102,241,0.18))"
                        fontSize="xl"
                      >
                        {section.emoji}
                      </Box>
                      <Heading as="h3" size="md" color="gray.800">
                        {section.title}
                      </Heading>
                    </HStack>

                    <VStack align="start" spacing={3} color="gray.600">
                      {section.items.map((item, index) => (
                        <HStack key={index} align="start" spacing={3}>
                          <Box mt={1} w={1.5} h={1.5} borderRadius="full" bg="blue.500" opacity={0.7} flexShrink={0} />
                          <Text lineHeight={1.6}>{item}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>

              <Box
                borderRadius="2xl"
                p={{ base: 6, md: 8 }}
                bg="linear-gradient(135deg, rgba(14,165,233,0.12), rgba(59,130,246,0.12))"
                border="1px solid rgba(59,130,246,0.2)"
              >
                <Stack spacing={4} align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} justify="space-between">
                  <Box>
                    <Heading as="h3" size="md" color="blue.700" mb={2}>
                      Délégué à la protection des données
                    </Heading>
                    <Text color="blue.700" opacity={0.85} maxW="2xl">
                      Pour toute question, demande d’exercice de droit ou signalement, contactez notre DPO à l’adresse : admin@groupe-solarenov.com ou via le formulaire de contact.
                    </Text>
                  </Box>
                  <Button
                    as="a"
                    href="/contact"
                    bg="blue.600"
                    color="white"
                    size="lg"
                    borderRadius="full"
                    px={10}
                    py={6}
                    _hover={{ bg: 'blue.500', transform: 'translateY(-2px)' }}
                    transition="all 0.2s ease"
                  >
                    Écrire au DPO
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default Confidentialite;
